import { preFillZeroList } from 'src/app/common/utils';
import {
  WalletAwardFormMode,
  AwardStatusEnum,
  EnrolmentType,
  AwardTypeEnum,
  RedemptionRefundSettingEnum,
} from '../../../../types/award-type';
import { FileObj } from './MultiFileUploader';
import { formatDay } from 'src/app/modules/pruwallet/comm';

export type AwardDetailProps = {
  code?: string;
  formMode: WalletAwardFormMode;
  detail?: GetWalletAwardDetail;
  onReload: () => void;
  onRouteTo: (route: string) => void;
};

export type ThumbnailImageItem = {
  blobId: string;
  name?: string;
  url?: string;
};

export type FileItem = {
  fileId?: string;
  fileName?: string;
  fileType?: string;
  fileUrl?: string;
};

type AgencyItem = {
  AGENCY?: string[];
};

export type AwardDetailsItem = Record<string, string>;
export type AwardDetailsWordcountItem = Record<string, number>;

export type WalletAwardEditItem = {
  awardId?: string;
  awardNameEn: string;
  awardNameZh: string;
  awardType?: AwardTypeEnum;
  creditDeductedAtSubmission?: boolean;
  redemptionRefundSetting?: string;
  creditDeductionType?: string;
  creditDeductionValue?: number | null;
  thumbnailUrl?: string;
  awardDetailsZh: string;
  awardDetailsEn: string;
  files: FileItem[];
  requireRedemption?: boolean;
  minCreditForRedemption?: number | null;
  creditDeductedAtRedemption?: number | null;
  redemptionStartDate?: string | null;
  redemptionEndDate?: string | null;
  awardValidityStartDate?: string | null;
  awardValidityEndDate?: string | null;
  promoCodeUsageLimit?: number | null;
  maxRedemptionPerUser?: number | null;
  displayRemainingQuota: boolean;
  eligibleUserType?: string;
  eligibleDesignations?: Record<string, AgencyItem>;
  eligibleUsers?: string[];
  status?: string;
  sendNotification: boolean;
  allowNegativeBalance: boolean;
  maximumNegativeCap?: number;
};

export const EDITABLE_FIELDS = [
  'thumbnail',
  'awardDetailsZh',
  'awardDetailsEn',
  'relatedFlyers',
  'redemptionDate',
  'awardValidityDate',
  'targetApplicants',
];

type UserCodeItem = {
  userCode?: string;
};

type DesignationItem = {
  designation?: string;
};

export type GetWalletAwardDetail = {
  awardId: string;
  awardNameEn?: string;
  awardNameZh?: string;
  awardType?: AwardTypeEnum;
  awardDetailsEn?: string;
  awardDetailsZh?: string;
  requireRedemption?: boolean;
  creditDeductedAtRedemption?: number;
  minCreditForRedemption?: number;
  creditDeductedAtSubmission?: boolean;
  redemptionRefundSetting?: string;
  creditDeductionType?: string;
  creditDeductionValue?: string;
  thumbnailUrl?: string;
  redemptionStartDate?: Date;
  redemptionEndDate?: Date;
  awardValidityStartDate?: Date;
  awardValidityEndDate?: Date;
  promoCodeUsageLimit?: number;
  files: FileItem[];
  maxRedemptionPerUser: number;
  displayRemainingQuota: boolean;
  status?: string;
  eligibleUserType?: string;
  eligibleUsers?: UserCodeItem[];
  eligibleDesignations?: Record<string, AgencyItem>;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
  sendNotification: boolean;
  allowNegativeBalance: boolean;
  maximumNegativeCap: number;
};

export type WalletAwardFormState = {
  awardId: string;
  awardNameEn: string;
  awardNameZh: string;
  awardType: AwardTypeEnum;
  creditDeductedAtSubmission: boolean;
  redemptionRefundSetting?: string;
  creditDeductionValue: string;
  thumbnail?: ThumbnailImageItem;
  awardDetailsZh: string;
  awardDetailsZhWordCount: number;
  awardDetailsEn: string;
  awardDetailsEnWordCount: number;
  relatedFlyers?: FileObj[];
  requireRedemption: boolean;
  minCreditForRedemption: string;
  creditDeductedAtRedemption: string;
  redemptionStartDate: Date | null;
  redemptionEndDate: Date | null;
  awardValidityStartDate: Date | null;
  awardValidityEndDate: Date | null;
  promoCodeUsageLimit: string;
  maxRedemptionPerUser: string;
  displayRemainingQuota: boolean;
  enrolmentType: EnrolmentType;
  eligibleDesignationsHK?: string[];
  eligibleDesignationsMO?: string[];
  agentListUploaded?: string[];
  status?: string;
  sendNotification: boolean;
  allowNegativeBalance: boolean;
  maximumNegativeCap: string;
};

export const initialState: WalletAwardFormState = {
  awardId: '',
  awardNameEn: '',
  awardNameZh: '',
  awardType: AwardTypeEnum.OTHERS,
  creditDeductedAtSubmission: false,
  redemptionRefundSetting: '',
  creditDeductionValue: '',
  thumbnail: { blobId: '' },
  awardDetailsZh: '',
  awardDetailsZhWordCount: 0,
  awardDetailsEn: '',
  awardDetailsEnWordCount: 0,
  relatedFlyers: undefined,
  requireRedemption: false,
  minCreditForRedemption: '',
  creditDeductedAtRedemption: '',
  redemptionStartDate: null,
  redemptionEndDate: null,
  awardValidityStartDate: null,
  awardValidityEndDate: null,
  promoCodeUsageLimit: '',
  maxRedemptionPerUser: '',
  displayRemainingQuota: false,
  enrolmentType: EnrolmentType.BY_SEGMENT,
  eligibleDesignationsHK: [],
  eligibleDesignationsMO: [],
  status: AwardStatusEnum.DRAFT,
  sendNotification: true,
  allowNegativeBalance: false,
  maximumNegativeCap: '0',
};

const targetApplicantToState = (detail: GetWalletAwardDetail) => {
  switch (detail.eligibleUserType) {
    case 'designation':
      return {
        enrolmentType: EnrolmentType.BY_SEGMENT,
        eligibleDesignationsHK: detail.eligibleDesignations?.HK?.AGENCY || [],
        eligibleDesignationsMO: detail.eligibleDesignations?.MO?.AGENCY || [],
        agentListUploaded: [],
      };
    case 'list':
      return {
        enrolmentType: EnrolmentType.SPECIFIC,
        eligibleDesignationsHK: [],
        eligibleDesignationsMO: [],
        agentListUploaded: detail.eligibleUsers?.map((item) => item.userCode || '') || [],
      };
    default:
      return {
        enrolmentType: EnrolmentType.BY_SEGMENT,
        eligibleDesignationsHK: [],
        eligibleDesignationsMO: [],
        agentListUploaded: [],
      };
  }
};

export const detailToStateConvertor = (detail: GetWalletAwardDetail): WalletAwardFormState => {
  return {
    awardId: detail?.awardId || '',
    awardNameEn: detail?.awardNameEn || '',
    awardNameZh: detail?.awardNameZh || '',
    awardType: detail?.awardType || AwardTypeEnum.OTHERS,
    creditDeductedAtSubmission: detail?.creditDeductedAtSubmission || false,
    redemptionRefundSetting: detail?.redemptionRefundSetting,
    creditDeductionValue: detail?.creditDeductionValue || '',
    thumbnail: { blobId: '' },
    awardDetailsZh: detail?.awardDetailsZh || '',
    awardDetailsZhWordCount: 0,
    awardDetailsEn: detail?.awardDetailsEn || '',
    awardDetailsEnWordCount: 0,
    relatedFlyers: [],
    requireRedemption: detail?.requireRedemption || false,
    minCreditForRedemption: numberToString(detail?.minCreditForRedemption),
    creditDeductedAtRedemption: numberToString(detail?.creditDeductedAtRedemption),
    redemptionStartDate: detail?.redemptionStartDate || null,
    redemptionEndDate: detail?.redemptionEndDate || null,
    awardValidityStartDate: detail?.awardValidityStartDate || null,
    awardValidityEndDate: detail?.awardValidityEndDate || null,
    promoCodeUsageLimit: numberToString(detail?.promoCodeUsageLimit),
    maxRedemptionPerUser: numberToString(detail?.maxRedemptionPerUser),
    displayRemainingQuota: detail?.displayRemainingQuota || false,
    ...targetApplicantToState(detail),
    status: detail?.status || AwardStatusEnum.DRAFT,
    sendNotification: detail?.sendNotification || false,
    allowNegativeBalance: detail?.allowNegativeBalance || false,
    maximumNegativeCap: numberToString(detail?.maximumNegativeCap),
  };
};

const getSubmitTargetApplicant = (formState: WalletAwardFormState) => {
  if (formState.enrolmentType === EnrolmentType.BY_SEGMENT) {
    return {
      eligibleUserType: 'designation',
      eligibleDesignations: {
        HK: {
          AGENCY: formState.eligibleDesignationsHK || [],
        },
        MO: {
          AGENCY: formState.eligibleDesignationsMO || [],
        },
      },
      eligibleUsers: [],
    };
  } else if (formState.enrolmentType === EnrolmentType.SPECIFIC) {
    return {
      eligibleUserType: 'list',
      eligibleDesignations: {
        HK: {
          AGENCY: [],
        },
        MO: {
          AGENCY: [],
        },
      },
      eligibleUsers: preFillZeroList(formState.agentListUploaded || []),
    };
  }
  return {};
};

export const getSubmitDetail = (formState: WalletAwardFormState) => {
  const isPromoCode = () => {
    return formState.awardType === AwardTypeEnum.PROMO_CODE;
  };
  const allowNegativeBalance =
    formState.awardType === AwardTypeEnum.PROMO_CODE &&
    formState.creditDeductedAtSubmission &&
    formState.requireRedemption
      ? formState.allowNegativeBalance
      : false;
  const details: WalletAwardEditItem = {
    awardId: formState.awardId,
    awardNameZh: formState.awardNameZh,
    awardNameEn: formState.awardNameEn,
    awardType: formState.awardType,
    creditDeductedAtSubmission: isPromoCode() ? formState.creditDeductedAtSubmission : false,
    redemptionRefundSetting: formState.redemptionRefundSetting || undefined,
    creditDeductionType: 'ratio',
    creditDeductionValue: formState.creditDeductedAtSubmission ? toNumber(formState.creditDeductionValue) : null,
    thumbnailUrl: formState.thumbnail?.blobId ? `res://${formState.thumbnail?.blobId}` : '',
    awardDetailsZh: formState.awardDetailsZh,
    awardDetailsEn: formState.awardDetailsEn,
    files: formState.relatedFlyers
      ? formState.relatedFlyers
          ?.filter((m: any) => !!m)
          .map((m: any) => {
            return { fileId: m.fileName, fileType: 'flyer', fileUrl: `res://${m.blobId}` };
          })
      : [],
    requireRedemption: formState.requireRedemption,
    minCreditForRedemption: formState.requireRedemption ? toNumber(formState.minCreditForRedemption) : null,
    creditDeductedAtRedemption: formState.requireRedemption ? toNumber(formState.creditDeductedAtRedemption) : null,
    redemptionStartDate: formState.redemptionStartDate ? formatDay(formState.redemptionStartDate) : null,
    redemptionEndDate: formState.redemptionEndDate ? formatDay(formState.redemptionEndDate) : null,
    awardValidityStartDate: formState.awardValidityStartDate ? formatDay(formState.awardValidityStartDate) : null,
    awardValidityEndDate: formState.awardValidityEndDate ? formatDay(formState.awardValidityEndDate) : null,
    promoCodeUsageLimit: isPromoCode() ? toNumber(formState.promoCodeUsageLimit) : null,
    maxRedemptionPerUser: formState.maxRedemptionPerUser ? toNumber(formState.maxRedemptionPerUser) : null,
    displayRemainingQuota: formState.displayRemainingQuota,
    ...getSubmitTargetApplicant(formState),
    status: formState.status,
    sendNotification: formState.requireRedemption ? formState.sendNotification : false,
    allowNegativeBalance,
    maximumNegativeCap: allowNegativeBalance
      ? isStrictlyNegative(formState.maximumNegativeCap)
        ? toNumber(formState.maximumNegativeCap)
        : 0
      : 0,
  };
  return details;
};

export type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof WalletAwardFormState;
    value: any;
  };
};
export type UploadThumbnailImageAction = {
  type: 'UPLOAD_THUMBNAIL_IMAGE';
  payload: {
    blobId: string;
    url: string;
    name: string;
  };
};

export type ToggleDesignationAction = {
  type: 'TOGGLE_DESIGNATION';
  payload: {
    value: string;
  };
};

export type WalletAwardFormAction = ModifyFieldAction;

export const walletAwardFormReducer = (
  state: WalletAwardFormState,
  action: WalletAwardFormAction | UploadThumbnailImageAction,
): WalletAwardFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'UPLOAD_THUMBNAIL_IMAGE':
      return {
        ...state,
        thumbnail: action.payload,
      };
  }
};

export const toNumber = (value?: string) => {
  if (value) {
    return Number(value);
  }
  return undefined;
};

export const numberToString = (value?: number) => {
  if (value !== undefined && value !== null) {
    return String(value);
  }
  return '';
};

export const isStrictlyNegative = (value: string | number | null | undefined): boolean => {
  if (!value) return false;

  const s = String(value).trim();
  if (s === '') return false;

  const n = Number(s);
  return !Number.isNaN(n) && n <= 0;
};
